import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PRICES, type PricesUpdate } from '@baerenfang/shared';
import {
  ApiClientError,
  getPrices,
  pruneHutDutySlots,
  rotateAdminPassword,
  rotateGuestPassword,
  setHutCapacity,
  setPrices,
} from '../../lib/api';
import { chfToRappen, rappenToChfString } from '../../lib/format';
import { DateRangePicker } from '../../components/DateRangePicker';
import { todayInZurich } from '../../lib/today';
import { useDateRange } from '../../lib/useDateRange';
import { PasswordInput } from '../../components/PasswordInput';

export function AdminSettingsTab() {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PasswordCard
        title={t('admin.settings.guestPassword')}
        action={(p) => rotateGuestPassword(p)}
        successKey="admin.settings.guestPasswordSaved"
      />
      <PasswordCard
        title={t('admin.settings.adminPassword')}
        action={(p) => rotateAdminPassword(p)}
        successKey="admin.settings.adminPasswordSaved"
      />
      <CapacityCard />
      <PricesCard />
      <HousekeepingCard />
    </div>
  );
}

function yesterdayInZurich(): string {
  const t = todayInZurich();
  const [y, m, d] = t.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d! - 1));
  return dt.toISOString().slice(0, 10);
}

function HousekeepingCard() {
  const { t } = useTranslation();
  const yesterday = yesterdayInZurich();
  const { from, to, reset } = useDateRange({
    initialFrom: '2020-01-01',
    initialTo: yesterday,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Both bounds must be strictly in the past. Mirrors the server-side
  // `check_out < today` guard so the warden can't even pick a future date
  // through the typed-input fallback.
  const inPast = from <= yesterday && to <= yesterday;
  const rangeValid = from <= to;
  const canSubmit = inPast && rangeValid && !busy;

  const submit = async () => {
    if (!rangeValid) {
      setMsg({ kind: 'err', text: t('errors.ERR_VALIDATION') });
      return;
    }
    if (!inPast) {
      setMsg({ kind: 'err', text: t('admin.settings.pruneMustBePast') });
      return;
    }
    if (!window.confirm(t('admin.settings.pruneConfirm', { from, to }))) return;
    setBusy(true);
    setMsg(null);
    try {
      const { deleted } = await pruneHutDutySlots(from, to);
      setMsg({ kind: 'ok', text: t('admin.settings.pruneSuccess', { count: deleted }) });
    } catch (e) {
      setMsg({
        kind: 'err',
        text:
          e instanceof ApiClientError ? t(`errors.${e.code}`, e.message) : t('errors.ERR_INTERNAL'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-4 space-y-3 md:col-span-2">
      <h2 className="text-base font-medium">{t('admin.settings.housekeeping')}</h2>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {t('admin.settings.pruneSlotsHelp')}
      </p>
      <div>
        <label className="text-sm min-w-0">
          <span className="block mb-1 text-slate-600 dark:text-slate-400">
            {t('admin.hutDuty.from')} – {t('admin.hutDuty.to')}
          </span>
          <DateRangePicker from={from} to={to} onChange={reset} max={yesterday} />
        </label>
      </div>
      {msg && (
        <p
          role={msg.kind === 'err' ? 'alert' : 'status'}
          className={
            'text-sm rounded-md px-3 py-2 border ' +
            (msg.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-200'
              : 'bg-rose-50 border-rose-300 text-rose-700 dark:bg-rose-950 dark:border-rose-700 dark:text-rose-300')
          }
        >
          {msg.text}
        </p>
      )}
      <button
        type="button"
        onClick={() => void submit()}
        disabled={!canSubmit}
        className="rounded-md border border-rose-300 bg-white text-rose-700 dark:border-rose-700 dark:bg-slate-900 dark:text-rose-300 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
      >
        {t('admin.settings.pruneSlots')}
      </button>
    </div>
  );
}

const PRICE_CATEGORIES = ['members_adult', 'members_child', 'guests_adult', 'guests_child'] as const;

function PricesCard() {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<keyof PricesUpdate, string>>({
    members_adult: rappenToChfString(DEFAULT_PRICES.members_adult),
    members_child: rappenToChfString(DEFAULT_PRICES.members_child),
    guests_adult: rappenToChfString(DEFAULT_PRICES.guests_adult),
    guests_child: rappenToChfString(DEFAULT_PRICES.guests_child),
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    getPrices(ctl.signal)
      .then((p) => {
        setValues({
          members_adult: rappenToChfString(p.members_adult),
          members_child: rappenToChfString(p.members_child),
          guests_adult: rappenToChfString(p.guests_adult),
          guests_child: rappenToChfString(p.guests_child),
        });
      })
      .catch(() => {
        // Keep the defaults already shown.
      });
    return () => ctl.abort();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const rappen: Partial<PricesUpdate> = {};
    for (const cat of PRICE_CATEGORIES) {
      const n = chfToRappen(values[cat]);
      if (!Number.isFinite(n) || n < 0) {
        setMsg({ kind: 'err', text: t('errors.ERR_VALIDATION') });
        return;
      }
      rappen[cat] = n;
    }
    setBusy(true);
    setMsg(null);
    try {
      await setPrices(rappen as PricesUpdate);
      setMsg({ kind: 'ok', text: t('admin.settings.pricesSaved') });
    } catch (e) {
      setMsg({
        kind: 'err',
        text:
          e instanceof ApiClientError ? t(`errors.${e.code}`, e.message) : t('errors.ERR_INTERNAL'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-4 space-y-3 md:col-span-2">
      <h2 className="text-base font-medium">{t('admin.settings.prices')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PRICE_CATEGORIES.map((cat) => (
          <label key={cat} className="text-sm block">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t(`admin.settings.priceCategory.${cat}`)}
            </span>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]+([.,][0-9]{1,2})?"
              autoComplete="off"
              value={values[cat]}
              onChange={(e) => setValues((v) => ({ ...v, [cat]: e.target.value }))}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm"
            />
          </label>
        ))}
      </div>
      {msg && (
        <p
          role={msg.kind === 'err' ? 'alert' : 'status'}
          className={
            'text-sm rounded-md px-3 py-2 border ' +
            (msg.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-200'
              : 'bg-rose-50 border-rose-300 text-rose-700 dark:bg-rose-950 dark:border-rose-700 dark:text-rose-300')
          }
        >
          {msg.text}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
      >
        {t('common.save')}
      </button>
    </form>
  );
}

function PasswordCard({
  title,
  action,
  successKey,
}: {
  title: string;
  action(password: string): Promise<void>;
  successKey: string;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setMsg({ kind: 'err', text: t('admin.settings.passwordTooShort') });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await action(password);
      setPassword('');
      setMsg({ kind: 'ok', text: t(successKey) });
    } catch (e) {
      setMsg({
        kind: 'err',
        text:
          e instanceof ApiClientError ? t(`errors.${e.code}`, e.message) : t('errors.ERR_INTERNAL'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-4 space-y-3">
      <h2 className="text-base font-medium">{title}</h2>
      <label className="text-sm block">
        <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.settings.newPassword')}</span>
        <PasswordInput
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm"
        />
      </label>
      {msg && (
        <p
          role={msg.kind === 'err' ? 'alert' : 'status'}
          className={
            'text-sm rounded-md px-3 py-2 border ' +
            (msg.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-200'
              : 'bg-rose-50 border-rose-300 text-rose-700 dark:bg-rose-950 dark:border-rose-700 dark:text-rose-300')
          }
        >
          {msg.text}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || !password}
        className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
      >
        {t('common.save')}
      </button>
    </form>
  );
}

function CapacityCard() {
  const { t } = useTranslation();
  const [capacity, setCapacity] = useState('26');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number.parseInt(capacity, 10);
    if (!Number.isFinite(n) || n < 0) {
      setMsg({ kind: 'err', text: t('errors.ERR_VALIDATION') });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await setHutCapacity(n);
      setMsg({ kind: 'ok', text: t('admin.settings.capacitySaved') });
    } catch (e) {
      setMsg({
        kind: 'err',
        text:
          e instanceof ApiClientError ? t(`errors.${e.code}`, e.message) : t('errors.ERR_INTERNAL'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-4 space-y-3">
      <h2 className="text-base font-medium">{t('admin.settings.hutCapacity')}</h2>
      <p className="text-xs text-slate-500 dark:text-slate-400">{t('admin.settings.hutCapacityHelp')}</p>
      <label className="text-sm block">
        <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.settings.defaultBeds')}</span>
        <input
          type="number"
          min={0}
          max={1000}
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm"
        />
      </label>
      {msg && (
        <p
          role={msg.kind === 'err' ? 'alert' : 'status'}
          className={
            'text-sm rounded-md px-3 py-2 border ' +
            (msg.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-200'
              : 'bg-rose-50 border-rose-300 text-rose-700 dark:bg-rose-950 dark:border-rose-700 dark:text-rose-300')
          }
        >
          {msg.text}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
      >
        {t('common.save')}
      </button>
    </form>
  );
}
